import React from "react";
import { Link } from "react-router-dom";

import { HStack, Text } from "@/components/ui";
import { cn } from "@/utils/cn";

interface StoreButtonProps {
  /** Store icon (an svg / <Icon> node, sized by the caller). */
  icon: React.ReactNode;
  /** Small label above the store name, e.g. "Download on the". */
  label: React.ReactNode;
  /** Bold store name, e.g. "App Store". */
  name: React.ReactNode;
  /** External URL — renders an `<a target="_blank">`. */
  href?: string;
  /** Internal route — renders a react-router `<Link>`. Takes precedence over `href`. */
  to?: string;
  onClick?: () => void;
  className?: string;
}

/** Pill-shaped app-store download button shared by all landing-page store links. */
const StoreButton: React.FC<StoreButtonProps> = ({
  icon,
  label,
  name,
  href,
  to,
  onClick,
  className,
}) => {
  const classes = cn(
    "flex items-center justify-center rounded-full border-2 bg-background px-3.5 py-2 text-foreground hover:bg-accent",
    className,
  );

  const content = (
    <HStack space={2} alignItems="center">
      {icon}
      <div>
        <Text size="xs" weight="medium" className="text-[0.5rem]! leading-2!">
          {label}
        </Text>
        <Text weight="bold" className="text-[0.75rem]! leading-4!">
          {name}
        </Text>
      </div>
    </HStack>
  );

  if (to) {
    return (
      <Link to={to} className={classes} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" className={classes} onClick={onClick}>
      {content}
    </a>
  );
};

export default StoreButton;
